import { ProfileThemeType } from 'shared/__generated__/graphql';
import { gql } from 'graphql-tag';
import { ThemeColorMap } from 'shared/misc/theme/custom/custom-theme-colors';
import NodeInMemoryCache from 'node-cache';

const inMemoryCache = new NodeInMemoryCache({
  stdTTL: 60,
});

import { ApolloClientRouterContext } from '@apollo/client-integration-tanstack-start';

export async function getInitialTheme(
  client: ApolloClientRouterContext['apolloClient'],
  user: any | null,
  pathname: string | null,
): Promise<{ theme: ProfileThemeType; customTheme: { colors: string } | null }> {
  if (pathname && inMemoryCache.has(pathname)) {
    const cache = inMemoryCache.get<ProfileThemeType>(pathname);
    if (cache && cache !== ProfileThemeType.Custom) {
      return {
        theme: cache,
        customTheme: null,
      };
    }
  }

  if (pathname?.startsWith('/u/')) {
    const username = pathname?.split('/')[2];
    const { data } = await client.query({
      query: gql`
        query getProfileTheme($username: String!) {
          user(username: $username) {
            profileTheme
            customTheme {
              colors
              updatedAt
            }
          }
        }
      `,
      variables: { username },
    });
    inMemoryCache.set(pathname!, data.user?.profileTheme!);
    return { theme: data.user?.profileTheme!, customTheme: data.user?.customTheme?.colors };
  }

  if (pathname?.startsWith('/rec/')) {
    const id = pathname?.split('/')[2];
    const { data } = await client.query({
      query: gql`
        query getRecViewTheme($id: String!) {
          rec(id: $id) {
            user {
              profileTheme
              customTheme {
                colors
                updatedAt
              }
            }
          }
        }
      `,
      variables: { id },
    });
    inMemoryCache.set(pathname!, data.rec?.user?.profileTheme!);
    return {
      theme: data.rec?.user?.profileTheme!,
      customTheme: data.rec?.user?.customTheme?.colors,
    };
  }

  if (pathname?.startsWith('/ask/')) {
    const id = pathname?.split('/')[2];
    const { data } = await client.query({
      query: gql`
        query getAskViewTheme($id: String!) {
          prompt(id: $id) {
            user {
              profileTheme
              customTheme {
                colors
                updatedAt
              }
            }
          }
        }
      `,
      variables: { id },
    });
    inMemoryCache.set(pathname!, data.prompt?.user?.profileTheme!);
    return {
      theme: data.prompt?.user?.profileTheme!,
      customTheme: data.prompt?.user?.customTheme?.colors,
    };
  }

  if (pathname?.startsWith('/p/')) {
    const name = pathname?.split('/')[2];
    const { data } = await client.query({
      query: gql`
        query getArticleTheme($name: String!) {
          article(id_or_slug: $name) {
            document {
              customTheme {
                colors
                updatedAt
              }
            }
          }
        }
      `,
      variables: { name },
    });
    if (data.article?.document?.customTheme) {
      inMemoryCache.set(pathname!, ProfileThemeType.Custom);
      return {
        theme: ProfileThemeType.Custom,
        customTheme: data.article?.document?.customTheme?.colors,
      };
    }
  }

  if (pathname?.startsWith('/event/edit/')) {
    const eventId = pathname?.split('/')[3];
    const { data } = await client.query({
      query: gql`
        query getEventTheme($eventId: String!) {
          event(eventId: $eventId) {
            customTheme {
              colors
              updatedAt
            }
            creator {
              profileTheme
              customTheme {
                colors
                updatedAt
              }
            }
          }
        }
      `,
      variables: { eventId },
    });
    if (data.event?.customTheme) {
      inMemoryCache.set(pathname!, ProfileThemeType.Custom);
      return {
        theme: ProfileThemeType.Custom,
        customTheme: data.event?.customTheme?.colors,
      };
    } else if (data.event?.creator?.profileTheme) {
      inMemoryCache.set(pathname!, data.event?.creator?.profileTheme);
      return {
        theme: ProfileThemeType.Custom,
        customTheme: data.event?.creator?.customTheme?.colors || ThemeColorMap[data.event?.creator?.profileTheme],
      };
    }
  }

  if (pathname?.startsWith('/event/') && !pathname?.startsWith('/event/edit/')) {
    const eventId = pathname?.split('/')[2];
    if (eventId === 'compose') {
      if (!user) return { theme: ProfileThemeType.Default, customTheme: null };
      return { theme: user.profileTheme!, customTheme: user.customTheme?.colors };
    }
    const { data } = await client.query({
      query: gql`
        query getEventTheme($eventId: String!) {
          event(eventId: $eventId) {
            customTheme {
              colors
              updatedAt
            }
            creator {
              profileTheme
              customTheme {
                colors
                updatedAt
              }
            }
          }
        }
      `,
      variables: { eventId },
    });
    if (data.event?.customTheme) {
      inMemoryCache.set(pathname!, ProfileThemeType.Custom);
      return {
        theme: ProfileThemeType.Custom,
        customTheme: data.event?.customTheme?.colors,
      };
    } else if (data.event?.creator?.profileTheme) {
      inMemoryCache.set(pathname!, data.event?.creator?.profileTheme);
      return {
        theme: ProfileThemeType.Custom,
        customTheme: data.event?.creator?.customTheme?.colors || ThemeColorMap[data.event?.creator?.profileTheme],
      };
    }
  }

  const isPiClassic =
    pathname &&
    [
      '/welcome',
      '/what-is-this',
      '/sign-up',
      '/sign-in',
      '/p',
      '/read',
      '/suggest',
      '/subscribe/referral',
      '/links',
      '/reset-password',
    ].some((pattern) => pathname?.startsWith(pattern));

  if (isPiClassic) {
    return { theme: ProfileThemeType.PiClassic, customTheme: null };
  }

  if (pathname?.startsWith('/sxsw-2025')) {
    return { theme: ProfileThemeType.Dark, customTheme: null };
  }

  if (!user) return { theme: ProfileThemeType.Default, customTheme: null };

  return { theme: user.profileTheme!, customTheme: user.customTheme?.colors };
}
